import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DraftAttachments — image / camera / file / voice quick-attach row
 * that sits in the omnibar's meta row, next to the destination chip.
 *
 * Surfaces four affordances:
 *
 *   - Image:   pick image(s) from the device's library
 *   - Camera:  open the rear camera on mobile (`capture="environment"`);
 *              on desktop this attribute is a no-op so the same file
 *              picker as Image opens — matches the convention used by
 *              chat composers (WhatsApp, Telegram, Signal) where the
 *              two buttons are visually distinct everywhere and
 *              behaviourally distinct only where the platform supports
 *              direct camera capture.
 *   - File:    pick any file type
 *   - Voice:   tap to start recording, tap again to stop. The actual
 *              recording lifecycle lives in `useVoiceRecorder` (mounted
 *              by `DraftBar`); this component just renders the button
 *              and routes its click to the parent's toggle callback.
 *              When `isRecording` is true the glyph swaps to a stop
 *              icon and the button takes on the brand-red recording
 *              fill (driven by the `data-recording` data attribute
 *              the SCSS reads).
 *
 * Selected files (image / camera / file branches) are forwarded to the
 * parent via `onFiles`. The parent — `DraftBar` — pushes them into
 * the staged-attachments list so the user can preview / remove them
 * before committing the capture; nothing uploads until send.
 *
 * The voice branch is intentionally one-way (button → toggle), with no
 * `File` produced here. The recorder owned by `DraftBar` emits the
 * final audio file through its own completion callback, decoupled
 * from the button's render cycle.
 */
import React, { memo, useCallback, useRef } from "react";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faImage, faCamera, faPaperclip, faMicrophone, faStop, } from "@fortawesome/pro-solid-svg-icons";
/**
 * Cap on a single picker action. Defence-in-depth — the staged list
 * has its own ceiling (`MAX_STAGED_ATTACHMENTS`) and `blockStore.add`
 * + the server-side ingest pipeline enforce theirs, but capping at
 * the picker boundary keeps a pathological multi-select (user picks
 * 200 photos) from spawning dozens of optimistic placeholders before
 * the lower layers reject the overflow.
 */
const MAX_PICKED_FILES_PER_ACTION = 10;
export const DraftAttachments = memo(({ onFiles, isRecording, onToggleRecording, }) => {
    const imageInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const fileInputRef = useRef(null);
    // Picker change handler. Curried so each `<input>` gets its own
    // closure that remembers which source-tag to forward, while still
    // sharing the bulk of the logic (cap, reset, callback).
    const handleInputChange = useCallback((source) => (e) => {
        const picked = e.target.files;
        if (picked === null || picked.length === 0)
            return;
        const files = Array.from(picked).slice(0, MAX_PICKED_FILES_PER_ACTION);
        // Reset the input BEFORE invoking the callback so the same file
        // can be picked again immediately — browsers suppress the
        // `change` event if the input's value hasn't changed between
        // pickers, so a user attaching, removing, and re-attaching the
        // same screenshot would otherwise get no feedback the second
        // time.
        e.target.value = "";
        onFiles(files, source);
    }, [onFiles]);
    // Each picker button is disabled while a recording is in flight —
    // the input row is occupied by the live waveform during that window
    // and we don't want to stage additional files mid-recording without
    // a clear visual to anchor them against. The mic button itself
    // stays enabled (it's the only way out of the recording state).
    const disableDuringRecording = isRecording;
    return (_jsxs("div", { className: "omnibar-draft__attachments", role: "group", "aria-label": "Attachments", children: [_jsx("button", { type: "button", className: "omnibar-draft__attachment-btn", "aria-label": "Take photo", title: "Take photo", tabIndex: -1, disabled: disableDuringRecording, onMouseDown: (e) => e.preventDefault(), onClick: () => cameraInputRef.current?.click(), children: _jsx(FaIcon, { icon: faCamera }) }), _jsx("button", { type: "button", className: "omnibar-draft__attachment-btn", "aria-label": "Attach file", title: "Attach file", tabIndex: -1, disabled: disableDuringRecording, onMouseDown: (e) => e.preventDefault(), onClick: () => fileInputRef.current?.click(), children: _jsx(FaIcon, { icon: faPaperclip }) }), _jsx("button", { type: "button", className: "omnibar-draft__attachment-btn", "data-recording": isRecording ? "" : undefined, "aria-label": isRecording ? "Stop recording" : "Record voice", "aria-pressed": isRecording, title: isRecording ? "Stop recording" : "Record voice", tabIndex: -1, onMouseDown: (e) => e.preventDefault(), onClick: onToggleRecording, children: _jsx(FaIcon, { icon: isRecording ? faStop : faMicrophone }) }), _jsx("input", { ref: cameraInputRef, type: "file", accept: "image/*", 
                // `capture="environment"` requests the rear-facing camera on
                // mobile devices that support it; on desktop the attribute is
                // a no-op and this input behaves identically to the image
                // picker above. The two buttons therefore read as distinct
                // affordances on mobile (gallery vs camera) and as redundant
                // copies on desktop — a known and accepted trade-off.
                capture: "environment", hidden: true, onChange: handleInputChange("camera") }), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, hidden: true, onChange: handleInputChange("file") })] }));
});
